import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, ScrollText, Feather } from "lucide-react";
import { api } from "@/lib/api";
import { IMAGES } from "@/data/content";
import { Reveal, Eyebrow, SectionTitle } from "@/components/common";

const WORK_META = {
  "holy-book": { icon: ScrollText, img: IMAGES.mandala, color: "text-indic-gold" },
  "indic-civilisation": { icon: BookOpen, img: IMAGES.templeCarved, color: "text-indic-sandstone" },
  "kalis-letters": { icon: Feather, img: IMAGES.goddess, color: "text-indic-terracotta" },
};

export default function Library() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/works").then((r) => setData(r.data)).catch(() => {}); }, []);

  return (
    <div className="pt-16 lg:pt-[72px]" data-testid="library-page">
      <section className="relative py-20 lg:py-28 border-b border-indic-gold/15">
        <img src={IMAGES.templeTower} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1021]/80 to-indic-indigo" />
        <div className="relative mx-auto max-w-[1400px] px-4 sm:px-8">
          <Eyebrow>The Digital Library</Eyebrow>
          <h1 className="mt-4 font-serif text-5xl lg:text-6xl font-semibold text-amber-50">Great Books</h1>
          <p className="mt-5 max-w-2xl text-lg text-slate-300/90 leading-relaxed">
            A living library of scripture, scholarship, and letters — fully read in-page, searchable, and free.
            {data && <span className="text-indic-gold"> {data.total_documents.toLocaleString()} sections across {data.works.length} works.</span>}
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {data?.works.map((w, i) => {
              const meta = WORK_META[w.slug] || WORK_META["indic-civilisation"];
              const Icon = meta.icon;
              return (
                <Reveal key={w.slug} delay={i * 0.1}>
                  <Link to={`/library/${w.slug}`} className="group relative flex flex-col h-full overflow-hidden rounded-sm border border-indic-card-border bg-indic-card hover:border-indic-gold/40 transition-colors" data-testid={`work-card-${w.slug}`}>
                    <div className="relative h-48 overflow-hidden">
                      <img src={meta.img} alt={w.title} className="h-full w-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-indic-card to-transparent" />
                      <Icon size={28} className={`absolute top-4 left-4 ${meta.color}`} />
                    </div>
                    <div className="flex flex-col flex-1 p-6">
                      <h3 className="font-serif text-2xl font-semibold text-amber-50">{w.title}</h3>
                      <p className="mt-1 font-serif italic text-indic-gold/80">{w.subtitle}</p>
                      <p className="mt-4 text-sm text-slate-300/85 leading-relaxed flex-1">{w.description}</p>
                      <div className="mt-6 flex items-center justify-between border-t border-indic-card-border pt-4">
                        <span className="text-xs font-sans uppercase tracking-[0.15em] text-slate-400">{w.doc_count} sections · {w.group_count} parts</span>
                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-indic-gold">Read <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
          {!data && <div className="text-center text-slate-400 py-20">Opening the library…</div>}
        </div>
      </section>
    </div>
  );
}
